import { Action, Reducer } from 'redux';
import * as Actions from '../actions';
import * as ActionEvents from '../actions/PersonPreferences.Actions';
import { PersonPreferenceState, PersonPreferenceUnloadState } from '../states/PersonPreferenceState';
import { PersonPreferenceViewModel } from '../../models/Entities/Person/Preference/PersonPreferenceModels';

export type KnownAction =
    | Actions.PersonPreferences.PersonPreferenceGetAllRequestAction
    | Actions.PersonPreferences.PersonPreferenceGetAllSuccessAction
    | Actions.PersonPreferences.PersonPreferenceGetAllFailureAction
    | Actions.PersonPreferences.PersonPreferenceGetByIdRequestAction
    | Actions.PersonPreferences.PersonPreferenceGetByIdSuccessAction
    | Actions.PersonPreferences.PersonPreferenceGetByIdFailureAction
    | Actions.PersonPreferences.PersonPreferenceCreateRequestAction
    | Actions.PersonPreferences.PersonPreferenceCreateSuccessAction
    | Actions.PersonPreferences.PersonPreferenceCreateFailureAction
    | Actions.PersonPreferences.PersonPreferenceUpdateRequestAction
    | Actions.PersonPreferences.PersonPreferenceUpdateSuccessAction
    | Actions.PersonPreferences.PersonPreferenceUpdateFailureAction
    | Actions.PersonPreferences.PersonPreferenceDeleteRequestAction
    | Actions.PersonPreferences.PersonPreferenceDeleteSuccessAction
    | Actions.PersonPreferences.PersonPreferenceDeleteFailureAction;

export const PersonPreferencesReducer: Reducer<PersonPreferenceState> = (
    state: PersonPreferenceState | undefined,
    incomingAction: Action
): PersonPreferenceState => {
    if (state === undefined) {
        return PersonPreferenceUnloadState as PersonPreferenceState;
    }

    const action = incomingAction as KnownAction;

    switch (action.type) {
        case ActionEvents.PERSON_PREFERENCE_GETALL_REQUEST:
            return {
                ...state,
                isLoadingAll: true,
                successLoadingAll: undefined,
                failOnLoadingAll: false,
                error: undefined
            };
        case ActionEvents.PERSON_PREFERENCE_GETALL_SUCCESS: {
            const otherPersons = state.list.filter((item: PersonPreferenceViewModel) => item.personId !== action.personId);
            return {
                ...state,
                isLoadingAll: false,
                successLoadingAll: true,
                failOnLoadingAll: false,
                list: [...otherPersons, ...action.payload],
                error: undefined
            };
        }
        case ActionEvents.PERSON_PREFERENCE_GETALL_FAILURE:
            return {
                ...state,
                isLoadingAll: false,
                successLoadingAll: false,
                failOnLoadingAll: true,
                error: action.error
            };

        case ActionEvents.PERSON_PREFERENCE_GETBYID_REQUEST:
            return {
                ...state,
                isLoadingOne: true,
                successLoadingOne: undefined,
                failOnLoadingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_PREFERENCE_GETBYID_SUCCESS: {
            const filtered = state.list.filter((item: PersonPreferenceViewModel) => item.id !== action.payload.id);
            return {
                ...state,
                isLoadingOne: false,
                successLoadingOne: true,
                failOnLoadingOne: false,
                list: [...filtered, action.payload],
                error: undefined
            };
        }
        case ActionEvents.PERSON_PREFERENCE_GETBYID_FAILURE:
            return {
                ...state,
                isLoadingOne: false,
                successLoadingOne: false,
                failOnLoadingOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_PREFERENCE_CREATE_REQUEST:
            return {
                ...state,
                isAddingNewOne: true,
                successAddingNewOne: undefined,
                failOnAddingNewOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_PREFERENCE_CREATE_SUCCESS: {
            const createdList = state.list.some((item: PersonPreferenceViewModel) => item.id === action.payload.id)
                ? state.list.map((item: PersonPreferenceViewModel) => (item.id === action.payload.id ? action.payload : item))
                : [...state.list, action.payload];
            return {
                ...state,
                isAddingNewOne: false,
                successAddingNewOne: true,
                failOnAddingNewOne: false,
                lastCreated: action.payload,
                list: createdList,
                error: undefined
            };
        }
        case ActionEvents.PERSON_PREFERENCE_CREATE_FAILURE:
            return {
                ...state,
                isAddingNewOne: false,
                successAddingNewOne: false,
                failOnAddingNewOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_PREFERENCE_UPDATE_REQUEST:
            return {
                ...state,
                isUpdatingOne: true,
                successUpdatingOne: undefined,
                failOnUpdatingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_PREFERENCE_UPDATE_SUCCESS: {
            const updatedList = state.list.some((item: PersonPreferenceViewModel) => item.id === action.payload.id)
                ? state.list.map((item: PersonPreferenceViewModel) => (item.id === action.payload.id ? { ...item, ...action.payload } : item))
                : [...state.list, action.payload];
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: true,
                failOnUpdatingOne: false,
                list: updatedList,
                error: undefined
            };
        }
        case ActionEvents.PERSON_PREFERENCE_UPDATE_FAILURE:
            return {
                ...state,
                isUpdatingOne: false,
                successUpdatingOne: false,
                failOnUpdatingOne: true,
                error: action.error
            };

        case ActionEvents.PERSON_PREFERENCE_DELETE_REQUEST:
            return {
                ...state,
                isDeletingOne: true,
                successDeletingOne: undefined,
                failOnDeletingOne: false,
                error: undefined
            };
        case ActionEvents.PERSON_PREFERENCE_DELETE_SUCCESS:
            return {
                ...state,
                isDeletingOne: false,
                successDeletingOne: true,
                failOnDeletingOne: false,
                list: state.list.filter((item: PersonPreferenceViewModel) => item.id !== action.id),
                error: undefined
            };
        case ActionEvents.PERSON_PREFERENCE_DELETE_FAILURE:
            return {
                ...state,
                isDeletingOne: false,
                successDeletingOne: false,
                failOnDeletingOne: true,
                error: action.error
            };
        default:
            return state;
    }
};
